import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Navigate } from "react-router-dom";
import { Plus, UserPlus, Mail, RefreshCw, Trash2, Edit3, Phone, Clock } from "lucide-react";
import Header from "../components/layout/Header";
import { StatCard, Badge, SearchInput, Button, Table } from "../components/ui/index";
import { usersAPI, cascadeAPI } from "../api/services";
import { formatDateTime } from "../utils/dateUtils";
import { useAuth } from "../context/AuthContext";

const roleBadge = {
  admin: "bg-red-100 text-red-700",
  revendeur: "bg-blue-100 text-blue-700",
  client: "bg-green-100 text-green-700",
  membre: "bg-purple-100 text-purple-700",
};

export default function Users() {
  const { t } = useTranslation();
  const getRoleLabel = (role) => t('users.roles.' + role) || role;
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterRole, setFilterRole] = useState("");
  const [filterStatut, setFilterStatut] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [toast, setToast] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [cardFilter, setCardFilter] = useState("all");

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (search) params.search = search;
      if (filterRole) params.role = filterRole;
      if (filterStatut) params.statut = filterStatut;
      const { data } = await usersAPI.getAll(params);
      setUsers(Array.isArray(data) ? data : data.data ?? []);
    } catch (err) {
      console.error("Erreur chargement users:", err);
    } finally {
      setLoading(false);
    }
  }, [search, filterRole, filterStatut]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    try {
      await usersAPI.remove(confirmDelete._id);
      setConfirmDelete(null);
      showToast(t('users.userDeleted'));
      fetchUsers();
    } catch (err) {
      showToast(err.response?.data?.error || "Erreur suppression", "error");
    }
  };

  const handleResendInvitation = async (user) => {
    try {
      await usersAPI.resendInvitation(user._id);
      showToast(`${t('users.invitationResent')} ${user.email}`);
    } catch (err) {
      showToast("Erreur renvoi invitation", "error");
    }
  };

  const handleToggleStatut = async (user) => {
    try {
      const newStatut = user.statut === "Actif" ? "Inactif" : "Actif";
      await usersAPI.update(user._id, { statut: newStatut });
      showToast(`Utilisateur ${newStatut === "Actif" ? "active" : "desactive"}`);
      fetchUsers();
    } catch (err) {
      showToast("Erreur mise a jour statut", "error");
    }
  };

  const actifs = users.filter(u => u.statut === "Actif").length;
  const admins = users.filter(u => u.role === "admin").length;
  const pending = users.filter(u => u.invitationExpires && new Date(u.invitationExpires) > new Date()).length;

  const toggleCard = (key) => setCardFilter(prev => prev === key ? "all" : key);
  const filteredUsers = cardFilter === "all" ? users
    : cardFilter === "actifs" ? users.filter(u => u.statut === "Actif")
    : cardFilter === "admins" ? users.filter(u => u.role === "admin")
    : cardFilter === "pending" ? users.filter(u => u.invitationExpires && new Date(u.invitationExpires) > new Date())
    : users;

  const selectStyle = "px-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500";

  if (currentUser?.role !== 'admin') return <Navigate to="/" replace />;

  return (
    <>
      <Header title={t('users.title')} subtitle={t('users.subtitle')} />

      {/* Toast */}
      {toast && (
        <div className={`fixed top-6 right-6 z-50 px-5 py-3 rounded-xl shadow-lg text-white text-sm font-medium ${toast.type === "error" ? "bg-red-500" : "bg-green-500"}`}>
          {toast.msg}
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-4 mb-6">
        <StatCard icon={UserPlus} iconBg="bg-blue-100 text-blue-600" label={t('users.total')} value={users.length} onClick={() => setCardFilter("all")} active={cardFilter === "all"} />
        <StatCard icon={UserPlus} iconBg="bg-green-100 text-green-600" label={t('users.active')} value={actifs} onClick={() => toggleCard("actifs")} active={cardFilter === "actifs"} />
        <StatCard icon={UserPlus} iconBg="bg-red-100 text-red-600" label={t('users.admins')} value={admins} onClick={() => toggleCard("admins")} active={cardFilter === "admins"} />
        <StatCard icon={Mail} iconBg="bg-amber-100 text-amber-600" label={t('users.pendingInvitations')} value={pending} onClick={() => toggleCard("pending")} active={cardFilter === "pending"} />
        <Button onClick={() => { setEditUser(null); setShowModal(true); }} className="h-full justify-center gap-2">
          <Plus size={18} /> {t('users.newUser')}
        </Button>
      </div>

      {/* Filtres */}
      <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100 mb-4">
        <div className="flex gap-3">
          <div className="flex-1">
            <SearchInput value={search} onChange={setSearch} placeholder="Rechercher par nom ou email..." />
          </div>
          <select value={filterRole} onChange={e => setFilterRole(e.target.value)} className={selectStyle}>
            <option value="">{t('users.allRoles')}</option>
            <option value="admin">{getRoleLabel('admin')}</option>
            <option value="revendeur">{getRoleLabel('revendeur')}</option>
            <option value="client">{getRoleLabel('client')}</option>
            <option value="membre">{getRoleLabel('membre')}</option>
          </select>
          <select value={filterStatut} onChange={e => setFilterStatut(e.target.value)} className={selectStyle}>
            <option value="">{t('users.allStatuses')}</option>
            <option value="Actif">{t('common.active')}</option>
            <option value="Inactif">{t('common.inactive')}</option>
          </select>
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex items-center justify-center py-20 text-slate-400 text-sm">{t('common.loading')}</div>
      ) : (
        <Table headers={[t('users.colUser'), t('users.colRole'), t('users.colAttachment'), t('users.colContact'), t('users.colLastLogin'), t('users.colStatus'), t('users.colActions')]}>
          {filteredUsers.map(u => {
            const isPending = u.invitationToken && u.invitationExpires && new Date(u.invitationExpires) > new Date();
            return (
            <tr key={u._id} className="hover:bg-gray-50 transition-colors">
              <td className="px-6 py-4">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-blue-100 flex items-center justify-center font-semibold text-blue-600 text-sm flex-shrink-0">
                    {u.nom?.split(" ").map(n => n[0]).join("").slice(0, 2).toUpperCase()}
                  </div>
                  <div>
                    <p className="font-semibold text-slate-800 text-sm">{u.nom}</p>
                  </div>
                </div>
              </td>
              <td className="px-6 py-4">
                <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium ${roleBadge[u.role] || "bg-gray-100 text-gray-600"}`}>
                  <div className="w-1.5 h-1.5 rounded-full bg-current opacity-60" />
                  {getRoleLabel(u.role)}
                </span>
              </td>
              <td className="px-6 py-4 text-sm text-slate-700">
                {u.revendeur?.nom || u.client?.nom || u.membre?.nom || "—"}
              </td>
              <td className="px-6 py-4">
                <div className="space-y-1">
                  <div className="flex items-center gap-1.5 text-xs text-slate-600"><Mail size={11} className="text-slate-400" /> {u.email}</div>
                  {u.telephone && <div className="flex items-center gap-1.5 text-xs text-slate-600"><Phone size={11} className="text-slate-400" /> {u.telephone}</div>}
                </div>
              </td>
              <td className="px-6 py-4">
                {u.lastLogin ? (
                  <div className="flex items-center gap-1.5 text-xs text-slate-600">
                    <Clock size={11} className="text-slate-400" /> {formatDateTime(u.lastLogin)}
                  </div>
                ) : (
                  <span className="text-xs text-slate-400">—</span>
                )}
              </td>
              <td className="px-6 py-4">
                {isPending ? (
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-700 border border-amber-200">
                    <Mail size={10} /> Invitation en attente
                  </span>
                ) : (
                  <Badge label={u.statut} />
                )}
              </td>
              <td className="px-6 py-4">
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => { setEditUser(u); setShowModal(true); }}
                    className="p-1.5 rounded-lg hover:bg-blue-50 text-blue-600 transition-colors"
                    title={t('users.edit')}
                  >
                    <Edit3 size={15} />
                  </button>
                  <button
                    onClick={() => handleResendInvitation(u)}
                    className="p-1.5 rounded-lg hover:bg-amber-50 text-amber-600 transition-colors"
                    title={t('users.resendInvitation')}
                  >
                    <RefreshCw size={15} />
                  </button>
                  <button
                    onClick={() => handleToggleStatut(u)}
                    className={`p-1.5 rounded-lg transition-colors text-xs font-medium px-2 ${u.statut === "Actif" ? "hover:bg-orange-50 text-orange-600" : "hover:bg-green-50 text-green-600"}`}
                    title={u.statut === "Actif" ? t('users.deactivate') : t('users.activate')}
                  >
                    {u.statut === "Actif" ? t('users.deactivate') : t('users.activate')}
                  </button>
                  <button
                    onClick={() => setConfirmDelete(u)}
                    className="p-1.5 rounded-lg hover:bg-red-50 text-red-500 transition-colors"
                    title={t('users.delete')}
                  >
                    <Trash2 size={15} />
                  </button>
                </div>
              </td>
            </tr>
          );
          })}
        </Table>
      )}

      {/* Modal confirmation suppression */}
      {confirmDelete && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center">
          <div className="bg-white rounded-2xl p-6 shadow-2xl max-w-md w-full mx-4">
            <h3 className="text-lg font-bold text-slate-800 mb-2">{t('users.deleteUser')}</h3>
            <p className="text-sm text-slate-500 mb-6">
              {t('users.confirmDelete')} <strong>{confirmDelete.nom}</strong> ({confirmDelete.email}) ? {t('users.irreversible')}
            </p>
            <div className="flex justify-end gap-3">
              <button onClick={() => setConfirmDelete(null)} className="px-4 py-2 rounded-xl text-sm font-medium text-slate-600 hover:bg-gray-100">
                {t('users.cancel')}
              </button>
              <button onClick={handleDelete} className="px-4 py-2 rounded-xl text-sm font-medium bg-red-500 text-white hover:bg-red-600">
                {t('users.delete')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal creation/edition */}
      {showModal && (
        <UserFormModal
          user={editUser}
          onClose={() => { setShowModal(false); setEditUser(null); }}
          onSave={() => { setShowModal(false); setEditUser(null); fetchUsers(); showToast(editUser ? "Utilisateur mis a jour" : "Invitation envoyee"); }}
        />
      )}
    </>
  );
}

function UserFormModal({ user, onClose, onSave }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    nom: user?.nom || "",
    email: user?.email || "",
    role: user?.role || "client",
    telephone: user?.telephone || "",
    revendeur: user?.revendeur?._id || user?.revendeur || "",
    client: user?.client?._id || user?.client || "",
  });
  const [revendeurs, setRevendeurs] = useState([]);
  const [clients, setClients] = useState([]);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    cascadeAPI.revendeurs().then(({ data }) => setRevendeurs(Array.isArray(data) ? data : data.data ?? [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (form.revendeur) {
      cascadeAPI.clients(form.revendeur).then(({ data }) => setClients(Array.isArray(data) ? data : data.data ?? [])).catch(() => {});
    } else {
      setClients([]);
    }
  }, [form.revendeur]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.nom || !form.email) {
      setError(t('users.nameEmailRequired'));
      return;
    }
    setSaving(true);
    setError("");
    try {
      const payload = { ...form };
      if (!payload.revendeur) delete payload.revendeur;
      if (!payload.client) delete payload.client;
      if (!payload.telephone) delete payload.telephone;

      if (user) {
        await usersAPI.update(user._id, payload);
      } else {
        await usersAPI.create(payload);
      }
      onSave();
    } catch (err) {
      setError(err.response?.data?.error || "Erreur");
    } finally {
      setSaving(false);
    }
  };

  const inputStyle = "w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500";
  const labelStyle = "block text-sm font-medium text-slate-700 mb-1.5";

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center">
      <div className="bg-white rounded-2xl p-6 shadow-2xl max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
        <h3 className="text-lg font-bold text-slate-800 mb-4">
          {user ? t('users.editUser') : t('users.newUserTitle')}
        </h3>

        {error && (
          <div className="mb-4 px-4 py-2.5 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">{error}</div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className={labelStyle}>{t('users.name')} *</label>
            <input value={form.nom} onChange={e => setForm(f => ({ ...f, nom: e.target.value }))} className={inputStyle} placeholder="Nom complet" />
          </div>

          <div>
            <label className={labelStyle}>{t('users.email')} *</label>
            <input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} className={inputStyle} placeholder="email@example.com" disabled={!!user} />
            {!user && <p className="text-xs text-slate-400 mt-1">{t('users.invitationEmail')}</p>}
          </div>

          <div>
            <label className={labelStyle}>{t('users.role')} *</label>
            <select value={form.role} onChange={e => setForm(f => ({ ...f, role: e.target.value, revendeur: "", client: "" }))} className={inputStyle}>
              <option value="admin">{t('users.roles.admin')}</option>
              <option value="revendeur">{t('users.roles.revendeur')}</option>
              <option value="client">{t('users.roles.client')}</option>
              <option value="membre">{t('users.roles.membre')}</option>
            </select>
          </div>

          <div>
            <label className={labelStyle}>{t('users.phone')}</label>
            <input value={form.telephone} onChange={e => setForm(f => ({ ...f, telephone: e.target.value }))} className={inputStyle} placeholder="+33..." />
          </div>

          {(form.role === "revendeur" || form.role === "client" || form.role === "membre") && (
            <div>
              <label className={labelStyle}>{t('users.reseller')}</label>
              <select value={form.revendeur} onChange={e => setForm(f => ({ ...f, revendeur: e.target.value, client: "" }))} className={inputStyle}>
                <option value="">{t('users.selectOption')}</option>
                {revendeurs.map(r => <option key={r._id} value={r._id}>{r.nom}</option>)}
              </select>
            </div>
          )}

          {form.role === "client" && (
            <div>
              <label className={labelStyle}>{t('users.client')}</label>
              <select value={form.client} onChange={e => setForm(f => ({ ...f, client: e.target.value }))} className={inputStyle}>
                <option value="">{t('users.selectOption')}</option>
                {clients.map(c => <option key={c._id} value={c._id}>{c.nom}</option>)}
              </select>
            </div>
          )}

          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2.5 rounded-xl text-sm font-medium text-slate-600 hover:bg-gray-100">
              {t('users.cancel')}
            </button>
            <button type="submit" disabled={saving} className="px-6 py-2.5 rounded-xl text-sm font-medium bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50">
              {saving ? "..." : user ? t('users.save') : t('users.invite')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
